/**
 * The ambient-audio toggle — the player's switch for a silent session.
 *
 * @remarks
 * R35 (D115): every layer above text is optional by ruling, and the ear is
 * its own axis (Calder's §8.1) — so this is a SIBLING of the Images toggle,
 * never a shared "presentation level". People play in offices; turned off,
 * the ambience layer makes no sound and decodes nothing new.
 *
 * A toggle, not a dialog, for the same reasons the Images toggle is — a
 * plain button carrying `aria-pressed`, naming its own state for assistive
 * tech rather than relying on the pressed state alone.
 *
 * @packageDocumentation
 */

/** Props for {@link AmbientAudioToggleComponent}. */
export interface AmbientAudioToggleComponentProps {
	/** Whether the ambience layer currently plays. */
	readonly isAudioEnabled: boolean;
	/** Called with the state the player is asking for. */
	readonly onToggle: (isAudioEnabled: boolean) => void;
}

/**
 * Render the sound on/off toggle.
 *
 * @param props - {@link AmbientAudioToggleComponentProps}
 * @returns The toggle button
 */
export const AmbientAudioToggleComponent = ({ isAudioEnabled, onToggle }: AmbientAudioToggleComponentProps) => (
	<button
		aria-label={isAudioEnabled ? 'Sound on — mute ambient audio' : 'Sound off — play ambient audio'}
		aria-pressed={isAudioEnabled}
		className="ambient-audio-toggle__trigger"
		onClick={() => {
			onToggle(!isAudioEnabled);
		}}
		type="button"
	>
		<AmbientAudioIcon isEnabled={isAudioEnabled} />
		<span className="ambient-audio-toggle__label">Sound</span>
	</button>
);

/**
 * The toggle's icon: a speaker, struck through when the layer is muted — the
 * state is visible without reading, exactly as the picture frame's is.
 *
 * @param props - Whether the ambience layer currently plays
 * @returns The icon
 */
const AmbientAudioIcon = ({ isEnabled }: { readonly isEnabled: boolean }) => (
	<svg
		aria-hidden="true"
		className="ambient-audio-toggle__icon"
		fill="none"
		focusable="false"
		height="16"
		stroke="currentColor"
		strokeLinecap="round"
		strokeLinejoin="round"
		strokeWidth={1.6}
		viewBox="0 0 24 24"
		width="16"
	>
		<path d="M11 5 6 9H3v6h3l5 4z" />
		{isEnabled ? <path d="M15.5 8.5a5 5 0 0 1 0 7M18 6a8.5 8.5 0 0 1 0 12" /> : <path d="M4 20 20 4" />}
	</svg>
);
